import React, { useEffect, useState } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { API } from 'utilities/api';
import { LIBRARY_LABELS, libraryHint, libraryLabel } from 'utilities/libraryLabels';

/**
 * Where a section's content comes from.
 *
 * Everything underneath this — picking which entries, how many, how they are
 * arranged, where a card links to — already existed, but only for a section
 * that was created as a library section in the first place. The choice was
 * made once in the Add dialog and could never be revisited, so a blog section
 * added from a template was a set of typed-in cards forever: the words sat on
 * the page rather than in the library, nothing linked to a post, and adding a
 * post changed nothing. That is what the template-built blog page was.
 *
 * So the binding is a property of the section, editable like any other.
 *
 * Switching between the two does not destroy anything either way. A static
 * section's components stay on the section while it is bound, untouched and
 * unread, and come back if it is unbound — which is what makes this safe to
 * try. The page holds a reference to library content, never a copy.
 */

/** Types that have at least one entry, so the list is not a catalogue of empties. */
const useCounts = (brandId) => {
  const [counts, setCounts] = useState(null);
  useEffect(() => {
    if (!brandId) return;
    API.getLibrarySummary({ brand_id: brandId })
      .then((res) => {
        const rows = Array.isArray(res?.types) ? res.types : [];
        setCounts(Object.fromEntries(rows.map((t) => [t.key, t.count ?? 0])));
      })
      // A failed count must not hide the types: it only costs the "3 posts"
      // note beside each one.
      .catch(() => setCounts({}));
  }, [brandId]);
  return counts;
};

export default function SectionSource({ source, onPatch, brandId }) {
  const counts = useCounts(brandId);
  const bound = source?.kind === 'library';
  const type = source?.libraryType || '';

  const bind = (nextType) => onPatch({
    source: {
      kind: 'library',
      libraryType: nextType,
      // The defaults the server would apply anyway, stated here so the
      // controls below have something to show immediately rather than
      // appearing empty until the first save.
      limit: source?.limit ?? 3,
      display: source?.display || 'grid',
      entryIds: [],
    },
  });

  const unbind = () => onPatch({ source: { kind: 'static' } });

  // Ordered so the ones a brand is most likely to want are not at the bottom
  // of twenty-six, but every type stays available.
  //
  // Products are held out of the count grouping rather than listed with the
  // rest: the summary this reads counts content-library collections, and
  // products are not one of them, so they would always have counted zero and
  // sat under "nothing in these yet" however full the shop was.
  const types = Object.keys(LIBRARY_LABELS).filter((k) => k !== 'products');
  const withEntries = counts ? types.filter((k) => counts[k] > 0) : [];
  const rest = types.filter((k) => !withEntries.includes(k));

  return (
    <Box mt='14px' pt='12px' sx={{ borderTop: '1px dashed', borderColor: 'divider' }}>
      <Typography
        variant='caption'
        fontWeight='700'
        color='text.secondary'
        textTransform='uppercase'
        display='block'
        mb='8px'
      >
        What this section shows
      </Typography>

      <Stack direction='row' gap='10px' flexWrap='wrap' alignItems='center'>
        <ToggleButtonGroup
          exclusive
          size='small'
          value={bound ? 'library' : 'static'}
          onChange={(_e, v) => {
            if (!v || (v === 'library') === bound) return;
            if (v === 'library') bind(type || withEntries[0] || 'blogs');
            else unbind();
          }}
        >
          <ToggleButton value='static'>Words typed here</ToggleButton>
          <ToggleButton value='library'>Content library</ToggleButton>
        </ToggleButtonGroup>

        {bound && (
          <FormControl size='small' sx={{ minWidth: 200 }}>
            <InputLabel id={`lib-type-${source?.libraryType || 'new'}`}>Which content</InputLabel>
            <Select
              labelId={`lib-type-${source?.libraryType || 'new'}`}
              label='Which content'
              value={type}
              // Changing the type clears the hand-picked entries: they are ids
              // from the old collection and would match nothing in the new
              // one, so keeping them would show an empty section with three
              // invisible choices still set.
              onChange={(e) => bind(e.target.value)}
            >
              {withEntries.length > 0 && [
                <MenuItem key='h1' disabled sx={{ opacity: 0.7, fontSize: 12 }}>
                  You have these
                </MenuItem>,
                ...withEntries.map((k) => (
                  <MenuItem key={k} value={k}>
                    {libraryLabel(k)}
                    <Typography component='span' variant='caption' color='text.secondary' ml='6px'>
                      {counts[k]}
                    </Typography>
                  </MenuItem>
                )),
                <MenuItem key='h2' disabled sx={{ opacity: 0.7, fontSize: 12 }}>
                  Nothing in these yet
                </MenuItem>,
              ]}
              {rest.map((k) => <MenuItem key={k} value={k}>{libraryLabel(k)}</MenuItem>)}
              <MenuItem key='h3' disabled sx={{ opacity: 0.7, fontSize: 12 }}>
                From your shop
              </MenuItem>
              <MenuItem value='products'>{libraryLabel('products')}</MenuItem>
            </Select>
          </FormControl>
        )}
      </Stack>

      <Typography variant='caption' color='text.secondary' display='block' mt='6px'>
        {bound
          ? libraryHint(type)
          : 'The words live on this page. Switch to the content library to show '
            + 'posts, news, jobs or anything else you keep there — written once '
            + 'and kept in step everywhere it appears.'}
      </Typography>
    </Box>
  );
}
